import React, { useState } from "react";
import { Link } from "wouter";
import { Seo } from "@/components/Seo";
import { Section, PageHeader, Eyebrow } from "@/components/Section";
import { Check, Minus, ChevronDown, ArrowRight } from "lucide-react";
import { APP_SIGN_UP_URL } from "@/lib/utils";
import {
  PLANS,
  TRIAL_DAYS,
  formatAud,
  annualEffectiveMonthlyCents,
  annualAmountCents,
  OVERAGE_LABEL,
  OVERAGE_UNIT_AMOUNT_CENTS,
} from "@workspace/plans";

type Cadence = "monthly" | "annual";

const COMPARISON: { group: string; rows: { label: string; values: [string | boolean, string | boolean, string | boolean] }[] }[] = [
  {
    group: "Volume",
    rows: [
      { label: "Verified requests / month included", values: ["20", "50", "100"] },
      { label: "Additional verifications", values: [OVERAGE_LABEL, OVERAGE_LABEL, OVERAGE_LABEL] },
      { label: "Workspaces", values: ["1", "Up to 3", "Unlimited"] },
      { label: "Staff seats", values: ["5", "Unlimited", "Unlimited"] },
    ],
  },
  {
    group: "Identity & branding",
    rows: [
      { label: "Per-workspace ConnectID", values: [true, true, true] },
      { label: "Co-branded client surface", values: [true, true, true] },
      { label: "Custom branding (logo + colours)", values: [false, true, true] },
      { label: "Custom domain on client surface", values: [false, true, true] },
      { label: "Custom form builder", values: [false, true, true] },
    ],
  },
  {
    group: "Audit & compliance",
    rows: [
      { label: "Append-only audit log", values: [true, true, true] },
      { label: "Quarterly audit export", values: [false, true, true] },
      { label: "Custom DPA", values: [false, false, true] },
      { label: "7-year retention of verification evidence", values: [true, true, true] },
    ],
  },
  {
    group: "Support",
    rows: [
      { label: "Email support", values: [true, true, true] },
      { label: "Priority support", values: [false, true, true] },
      { label: "Dedicated customer success", values: [false, false, true] },
      { label: "SLA-backed uptime", values: [false, false, true] },
    ],
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is there a free trial?",
    a: `Yes — the Growth plan includes a ${TRIAL_DAYS}-day free trial (capped at 10 requests) so you can configure your ConnectID credentials, set up custom branding, and run real verified requests before committing. We collect payment details up-front but do not charge until the trial ends, and you can cancel any time. Starter subscribes directly without a trial. Scale also has no trial — upgrade from Growth at any time from your workspace billing page.`,
  },
  {
    q: "What happens if I exceed my plan's included verifications?",
    a: `Additional verifications are billed at ${formatAud(OVERAGE_UNIT_AMOUNT_CENTS)} each on your next invoice. We never hard-cut a client mid-flow — the platform stays available and we meter the overage. Switch to a higher plan from your workspace billing page whenever you outgrow the included amount.`,
  },
  {
    q: "Do you offer an annual discount?",
    a: "Yes — annual billing saves 10% versus paying monthly, with one upfront invoice for the year. Toggle between monthly and annual on the pricing cards above to compare.",
  },
  {
    q: "Can I bring my own ConnectID accreditation?",
    a: "Yes. Every workspace stores its own SSA, signing key and mTLS material. Credentials are wrapped at rest with AES-256-GCM and the platform never returns secret material to the API. We do not share credentials between workspaces.",
  },
  {
    q: "Where is data stored?",
    a: "Production data is stored on Australian-based servers in the Sydney region. Backups are encrypted and region-locked. Transactional email and SMS delivery uses sub-processors who receive only minimum contact details, under APP-aligned contractual safeguards. See the Security page for the full data residency story.",
  },
  {
    q: "How long do you retain verification evidence?",
    a: "Verification events and the audit log are retained for 7 years to support your obligations under the Privacy Act 1988 and relevant industry regulations (trust-account audits, payroll record-keeping, claims documentation, etc.).",
  },
  {
    q: "How do I cancel?",
    a: "Cancel any time from workspace billing. Your data and audit log remain accessible during the current billing period; you can export the audit log at any point.",
  },
  {
    q: "Do you offer custom contracts?",
    a: "Scale customers get a custom DPA, MSA and security questionnaire on request. Reach out via our contact form (topic: Security review) and we'll send the pre-filled SIG-Lite and architecture overview.",
  },
  {
    q: "We're not in real estate. Does FortifyID work for us?",
    a: "Yes. The same product is used by conveyancers, law firms, payroll teams, accountants and insurers — anyone collecting bank, contact or instruction changes from clients. See the Industries pages for vertical-specific examples.",
  },
];

export default function Pricing() {
  const [cadence, setCadence] = useState<Cadence>("monthly");

  return (
    <>
      <Seo
        title="Pricing"
        description="Simple AUD pricing for verified change requests. Starter, Growth and Scale tiers — per-workspace ConnectID and full audit trail on every plan, with a 14-day free trial on Growth."
        path="/pricing"
      />
      <Section>
        <PageHeader
          eyebrow="Pricing"
          title="Pricing built around verified requests."
          lede="No per-seat tax. Every plan includes per-workspace ConnectID, the co-branded client surface, and the full audit trail. Start free for 14 days on Growth."
        />

        <div className="mt-8 rounded-xl border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] px-6 py-5 grid sm:grid-cols-[auto,1fr] gap-4 items-center">
          <div className="text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-forest))] leading-none whitespace-nowrap">
            $80k+
          </div>
          <p className="text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">
            The median cost of a single cyber incident for an Australian business. The Scale plan costs less than 0.3% of that per year — and one prevented redirection pays for it many times over.
          </p>
        </div>

        <div className="mt-8 flex justify-center">
          <CadenceToggle cadence={cadence} onChange={setCadence} />
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {PLANS.map((p) => {
            const monthly = p.monthlyAmountCents;
            const displayMonthly =
              cadence === "monthly" ? monthly : annualEffectiveMonthlyCents(p);
            const annualTotal = annualAmountCents(p);
            return (
              <div
                key={p.slug}
                className={
                  "rounded-2xl border p-8 flex flex-col " +
                  (p.highlight
                    ? "bg-[hsl(var(--brand-forest))] border-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))]"
                    : "bg-white/50 border-[hsl(var(--brand-sand))]")
                }
                data-testid={`card-plan-${p.slug}`}
              >
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase opacity-80">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-semibold" data-testid={`price-${p.slug}`}>
                    {formatAud(displayMonthly)}
                  </span>
                  <span className="text-sm opacity-70">/ month</span>
                </div>
                <div className={"mt-1 text-xs " + (p.highlight ? "text-[hsl(var(--brand-sand))]" : "text-[hsl(var(--brand-slate))]")}>
                  {cadence === "annual"
                    ? `Billed ${formatAud(annualTotal)} / year (save 10%)`
                    : "Billed monthly · cancel anytime"}
                </div>
                <p className={"mt-3 text-sm leading-relaxed " + (p.highlight ? "text-[hsl(var(--brand-sand))]" : "text-[hsl(var(--brand-slate))]")}>
                  {p.blurb}
                </p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={"w-4 h-4 mt-0.5 shrink-0 " + (p.highlight ? "text-[hsl(var(--brand-brass))]" : "text-[hsl(var(--brand-forest))]")} />
                      <span>{f}</span>
                    </li>
                  ))}
                  <li className={"flex items-start gap-2 text-xs pt-1 " + (p.highlight ? "text-[hsl(var(--brand-sand))]" : "text-[hsl(var(--brand-slate))]")}>
                    <span className="font-mono opacity-70">+</span>
                    <span>{OVERAGE_LABEL} above plan</span>
                  </li>
                </ul>
                <a
                  href={`${APP_SIGN_UP_URL}?plan=${p.slug}&cadence=${cadence}`}
                  className={
                    "mt-8 text-center px-5 py-3 rounded font-medium transition-colors " +
                    (p.highlight
                      ? "bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))] hover:bg-[hsl(42,55%,52%)]"
                      : "bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] hover:bg-[hsl(168,25%,22%)]")
                  }
                  data-testid={`button-plan-${p.slug}`}
                >
                  {p.trial ? `Start ${TRIAL_DAYS}-day free trial` : `Subscribe to ${p.name}`}
                </a>
              </div>
            );
          })}
        </div>
        <div className="mt-6 rounded-2xl border border-dashed border-[hsl(var(--brand-sand))] p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between bg-white/30">
          <div>
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))] mb-2">Enterprise</div>
            <h3 className="text-xl font-semibold text-[hsl(var(--brand-charcoal))]">Larger scale? Custom requirements?</h3>
            <p className="mt-2 text-sm text-[hsl(var(--brand-slate))] max-w-lg leading-relaxed">
              For franchise networks, national firms, and platforms integrating FortifyID into their own products — custom request volumes, multi-workspace administration, dedicated implementation support, SLA, MSA, and custom DPA on request.
            </p>
          </div>
          <div className="shrink-0">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors whitespace-nowrap"
            >
              Talk to us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <p className="mt-6 text-xs text-[hsl(var(--brand-slate))] font-mono tracking-wide">
          All plans billed in AUD, exclusive of GST. Cancel any time from your workspace billing page.
        </p>
      </Section>

      <Section className="!pt-0">
        <div className="border-t border-[hsl(var(--brand-sand))] pt-12">
          <Eyebrow>Compare plans</Eyebrow>
          <div className="mt-6 overflow-x-auto rounded-xl border border-[hsl(var(--brand-sand))] bg-white/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))]">
                  <th className="text-left font-medium px-5 py-3 text-[hsl(var(--brand-slate))]">&nbsp;</th>
                  {PLANS.map((p) => (
                    <th key={p.slug} className="text-left font-semibold px-5 py-3 text-[hsl(var(--brand-charcoal))]">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((group) => (
                  <React.Fragment key={group.group}>
                    <tr className="bg-[hsl(var(--brand-bone))]">
                      <td colSpan={4} className="px-5 py-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">
                        {group.group}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={`${group.group}-${row.label}`} className="border-t border-[hsl(var(--brand-sand))]">
                        <td className="px-5 py-3 text-[hsl(var(--brand-charcoal))]">{row.label}</td>
                        {row.values.map((v, i) => (
                          <td key={i} className="px-5 py-3 text-[hsl(var(--brand-slate))]">
                            {typeof v === "boolean" ? (
                              v ? (
                                <Check className="w-4 h-4 text-[hsl(var(--brand-forest))]" aria-label="Included" />
                              ) : (
                                <Minus className="w-4 h-4 text-[hsl(var(--brand-sand))]" aria-label="Not included" />
                              )
                            ) : (
                              <span>{v}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="border-t border-[hsl(var(--brand-sand))] pt-12">
          <Eyebrow>Frequently asked</Eyebrow>
          <Accordion items={FAQS} />
        </div>
      </Section>
    </>
  );
}

function CadenceToggle({ cadence, onChange }: { cadence: Cadence; onChange: (c: Cadence) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Billing cadence"
      className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--brand-sand))] bg-white/70 p-1"
    >
      {(["monthly", "annual"] as const).map((c) => {
        const active = cadence === c;
        return (
          <button
            key={c}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(c)}
            className={
              "px-4 py-1.5 text-sm rounded-full transition-colors " +
              (active
                ? "bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))]"
                : "text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-charcoal))]")
            }
            data-testid={`button-cadence-${c}`}
          >
            {c === "monthly" ? "Monthly" : "Annual"}
            {c === "annual" && (
              <span className={"ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded " + (active ? "bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))]" : "bg-[hsl(var(--brand-sand))] text-[hsl(var(--brand-charcoal))]")}>
                −10%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Accordion({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="mt-6 rounded-xl border border-[hsl(var(--brand-sand))] bg-white/50 divide-y divide-[hsl(var(--brand-sand))]">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-[hsl(var(--brand-bone))] transition-colors"
              data-testid={`faq-trigger-${i}`}
            >
              <span className="font-medium text-[hsl(var(--brand-charcoal))]">{item.q}</span>
              <ChevronDown
                className={"w-4 h-4 shrink-0 text-[hsl(var(--brand-slate))] transition-transform " + (isOpen ? "rotate-180" : "")}
              />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 text-sm text-[hsl(var(--brand-slate))] leading-relaxed" data-testid={`faq-content-${i}`}>
                {item.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
